import { useState } from "react";
import { Loader2, Plus, Settings2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useCreateInboxView, useUpdateInboxView } from "@/hooks/inboxView/useInboxViews";
import { inboxViewTitle, isInboxViewSorting } from "@/lib/inboxView";
import type { InboxView, InboxViewTimeRange } from "@/types/inboxView/inboxView";

const NEW_VIEW_TIME_RANGE: InboxViewTimeRange = { kind: "rolling", value: "last_30_days" };

interface InboxViewChipsProps {
  views: InboxView[];
  value: string | null;
  onChange: (viewId: string | null) => void;
  /** The genie the inbox is filtered to, so a view made from here reads that genie's conversations. */
  agentId: string | null;
  onManage?: () => void;
}

/**
 * Saved views as one-tap inbox filters, so a team can work its own slice
 * (parts, sales, service) from the Conversations list. A view is a chip when
 * it is set to show in the inbox, which is the view's pin.
 */
export function InboxViewChips({ views, value, onChange, agentId, onManage }: InboxViewChipsProps) {
  const pinned = views.filter((v) => v.pinned);
  const selected = value ? views.find((v) => v.id === value) : undefined;
  const shown = selected && !selected.pinned ? [...pinned, selected] : pinned;

  const segmentClass = (active: boolean) =>
    `min-h-[44px] sm:min-h-0 max-w-[16rem] shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 whitespace-nowrap text-sm transition-colors ${
      active ? "bg-gray-100 text-gray-900 font-semibold" : "text-gray-600 hover:bg-gray-50"
    }`;

  return (
    <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
      <div className="flex min-w-max items-center gap-2">
        <div className="inline-flex p-px rounded-xl bg-[#E5EAF0]">
          <div className="flex items-stretch rounded-[11px] bg-white overflow-hidden" role="tablist" aria-label="Views">
            <span className="flex items-center gap-1.5 border-r border-[#E5EAF0] px-3 text-xs font-bold uppercase tracking-wider text-[#64748B]">
              <GenieIcon name="eye" size={14} color="#4E9CFF" label="" />
              Views
            </span>
            <button
              type="button"
              role="tab"
              aria-selected={value === null}
              onClick={() => onChange(null)}
              className={segmentClass(value === null)}
            >
              All conversations
            </button>
            {shown.map((view) => {
              const title = inboxViewTitle(view);
              return (
                <button
                  key={view.id}
                  type="button"
                  role="tab"
                  aria-selected={value === view.id}
                  onClick={() => onChange(view.id)}
                  title={title}
                  className={segmentClass(value === view.id)}
                >
                  {isInboxViewSorting(view) && <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-genie-primary" />}
                  <span className="truncate">{title}</span>
                </button>
              );
            })}
          </div>
        </div>

        <NewViewChip agentId={agentId} onCreated={onChange} />
        {views.length > 0 && <ShowInInboxEditor views={views} onManage={onManage} />}

        {shown.length === 0 && (
          <span className="hidden md:inline text-[13px] text-[#64748B]">
            Sort your inbox by topic: describe a group like &ldquo;parts enquiries&rdquo; and your genie sorts
            conversations into it.
          </span>
        )}
      </div>
    </div>
  );
}

const softChipClass =
  "min-h-[44px] sm:min-h-[32px] shrink-0 inline-flex items-center gap-1.5 px-2.5 rounded-lg text-[13px] font-semibold text-genie-primary hover:bg-genie-primary/5 transition-colors";

const NewViewChip = ({ agentId, onCreated }: { agentId: string | null; onCreated: (viewId: string) => void }) => {
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const create = useCreateInboxView();
  const update = useUpdateInboxView();
  const pending = create.isPending || update.isPending;

  const submit = () => {
    const text = prompt.trim();
    if (!text || pending) return;
    create.mutate(
      { prompt: text, agentFilter: agentId ? [agentId] : null, timeRange: NEW_VIEW_TIME_RANGE },
      {
        onSuccess: ({ view }) => {
          update.mutate({ viewId: view.id, pinned: true });
          onCreated(view.id);
          setPrompt("");
          setOpen(false);
          toast.success("Your genie is sorting your conversations into this view. They appear here as it goes.");
        },
        onError: () => toast.error("Couldn't create the view. Try again."),
      },
    );
  };

  return (
    <Popover open={open} onOpenChange={(next) => !pending && setOpen(next)}>
      <PopoverTrigger asChild>
        <button type="button" className={softChipClass}>
          <Plus className="h-3.5 w-3.5" />
          New view
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={6} className="w-[min(24rem,calc(100vw-2rem))] p-4">
        <label htmlFor="inbox-new-view" className="block text-sm font-semibold text-[#222B45]">
          Which conversations belong together?
        </label>
        <p className="mt-0.5 text-xs text-[#64748B]">Describe them in your own words. Your genie reads each conversation and sorts it in.</p>
        <Textarea
          id="inbox-new-view"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          placeholder="e.g. Parts enquiries: the caller asks about a spare part, its price or if it's in stock"
          rows={3}
          disabled={pending}
          className="mt-3 text-base sm:text-sm"
        />
        <div className="mt-3 flex justify-end">
          <Button
            onClick={submit}
            disabled={!prompt.trim() || pending}
            className="bg-gradient-to-r from-genie-primary to-genie-secondary hover:from-genie-primary-hover hover:to-genie-secondary text-white font-semibold shadow-lg shadow-genie-primary/30"
          >
            {pending && <Loader2 className="h-4 w-4 animate-spin" />}
            {pending ? "Reading your conversations..." : "Create view"}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

const ShowInInboxEditor = ({ views, onManage }: { views: InboxView[]; onManage?: () => void }) => {
  const update = useUpdateInboxView();
  const [pendingId, setPendingId] = useState<string | null>(null);

  const toggle = (view: InboxView) => {
    setPendingId(view.id);
    update.mutate(
      { viewId: view.id, pinned: !view.pinned },
      {
        onError: () => toast.error("Couldn't change that view. Try again."),
        onSettled: () => setPendingId(null),
      },
    );
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          title="Choose which views show here"
          aria-label="Choose which views show here"
          className="min-h-[44px] min-w-[44px] sm:min-h-[32px] sm:min-w-[32px] shrink-0 inline-flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-50 hover:text-genie-primary transition-colors"
        >
          <Settings2 className="h-4 w-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={6} className="w-[min(22rem,calc(100vw-2rem))] p-0">
        <div className="px-4 pt-3 pb-2 border-b border-[#E5EAF0]">
          <div className="text-sm font-semibold text-[#222B45]">Show in inbox</div>
          <p className="text-xs text-[#64748B]">Pick the views you work from. They show as filters above your conversations.</p>
        </div>
        <ul className="max-h-72 overflow-y-auto py-1">
          {views.map((view) => {
            const title = inboxViewTitle(view);
            return (
              <li key={view.id}>
                <label className="flex min-h-[44px] cursor-pointer items-center gap-3 px-4 py-2 hover:bg-[#FAFCFF]">
                  <span className="min-w-0 flex-1 truncate text-sm text-[#222B45]" title={title}>
                    {title}
                  </span>
                  {pendingId === view.id ? (
                    <Loader2 className="h-4 w-4 animate-spin text-genie-primary" />
                  ) : (
                    <Switch checked={view.pinned} onCheckedChange={() => toggle(view)} disabled={pendingId !== null} />
                  )}
                </label>
              </li>
            );
          })}
        </ul>
        {onManage && (
          <div className="border-t border-[#E5EAF0] px-4 py-2">
            <button type="button" onClick={onManage} className="min-h-[44px] sm:min-h-0 text-sm text-genie-primary hover:underline">
              Manage views
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
